import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { pavoApiService, type PavoDevice } from '../../../../services/pavoApiService';

interface PavoRestP2PDeviceSelectDialogProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  branchId?: string;
  currentDeviceCode?: string | null;
  onSelect: (device: PavoDevice) => void;
}

const PavoRestP2PDeviceSelectDialog: React.FC<PavoRestP2PDeviceSelectDialogProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  currentDeviceCode,
  onSelect,
}) => {
  const [devices, setDevices] = useState<PavoDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const all = await pavoApiService.getPavoDevices(companyId, branchId);
      const restP2P = all.filter(d => d.deviceMode === 'pavo_rest_p2p');
      restP2P.sort((a, b) => (a.pavoNumber ?? 999) - (b.pavoNumber ?? 999));
      setDevices(restP2P);
    } catch (e) {
      console.error('[PavoRestP2PDeviceSelectDialog] Cihazlar yüklenemedi:', e);
      setError('Cihazlar yüklenirken bir hata oluştu.');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId]);

  useEffect(() => {
    if (open) loadDevices();
  }, [open, loadDevices]);

  const handleSelect = (device: PavoDevice) => {
    onSelect(device);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PointOfSaleIcon sx={{ color: '#7c4dff' }} />
          <Typography variant="h6" component="span">
            Rest-P2P Cihazı Seç
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={loadDevices} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Bu ekrana bağlanacak Rest-P2P modunda çalışan Pavo cihazını seçin.
          Nakit, POS ve Karma ödemeler seçilen cihaza SignalR üzerinden gönderilecek.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Rest-P2P modunda kayıtlı cihaz bulunamadı.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cihaz Yönetimi sayfasından "Rest-P2P Modu" seçerek yeni bir PavoPay cihazı ekleyin.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {devices.map((device) => {
              const isSelected = device.deviceCode === currentDeviceCode;
              return (
                <Card
                  key={device.deviceCode}
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderColor: isSelected ? '#7c4dff' : undefined,
                    borderWidth: isSelected ? 2 : 1,
                    bgcolor: isSelected ? 'rgba(124,77,255,0.04)' : undefined,
                    '&:hover': {
                      borderColor: '#7c4dff',
                      bgcolor: 'rgba(124,77,255,0.06)',
                    },
                  }}
                  onClick={() => handleSelect(device)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: '#7c4dff',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        #{device.pavoNumber ?? '?'}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {device.deviceName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Rest-P2P • {device.deviceCode}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={
                          <FiberManualRecordIcon
                            sx={{ fontSize: 10, color: device.isOnline ? 'success.main' : 'text.disabled' }}
                          />
                        }
                        label={device.isOnline ? 'Online' : 'Offline'}
                        size="small"
                        variant="outlined"
                        color={device.isOnline ? 'success' : 'default'}
                      />
                      {isSelected && (
                        <CheckCircleIcon sx={{ color: '#7c4dff', fontSize: 22 }} />
                      )}
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PavoRestP2PDeviceSelectDialog;
